import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import {
  ArrowRight,
  BriefcaseBusiness,
  Code2,
  FolderKanban,
  HeartHandshake,
  MessageSquareText,
  Sparkles,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Testimonials from "../Impact/Testimonials";
import reviews from "../Impact/data";

export default function Stats() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const focusAreas = [
    {
      icon: BriefcaseBusiness,
      title: "Travis County SYEP Internship Partnership",
      body: "Multi-year internships where youth build real products and prepare for careers in the digital economy.",
    },
    {
      icon: HeartHandshake,
      title: "Senior Digital Inclusion & Technology",
      body: "Workshops and one-on-one support that build confidence, connection, and practical digital skills for seniors.",
    },
    {
      icon: Users,
      title: "Ambassador Community Initiatives",
      body: "Community ambassadors trained to lead neighborhood projects, mentor peers, and expand local impact.",
    },
  ];

  const projectExamples = [
    "Web applications",
    "Community platforms",
    "Educational tools",
    "Interactive websites",
    "AI experiments",
    "UI/UX design",
    "Team software projects",
  ];

  const syepOutcomeStats = [
    { value: "3 Years", label: "Consecutive Travis County SYEP partnership" },
    { value: "60+", label: t("impactStats.syepOutcome.youthEngaged", { defaultValue: "Youth engaged" }) },
    { value: "100+", label: "Technology projects completed" },
    { value: "7 Weeks", label: "Internship model" },
    { value: "95%", label: "Rated Excellent/Good" },
    { value: "100%", label: "Would recommend" },
  ];

  const techSkills = [
    "Web Development",
    "App Development",
    "JavaScript | HTML | CSS | NextJs | TypeScript | React",
    "Git & GitHub | Git Copilot | Node.js",
    "Firebase | Supabase | Postgres",
    "UI/UX Design",
    "AI Tools",
    "Product Design & Testing",
    "Problem Solving",
  ];

  const professionalSkills = [
    "Communication",
    "Collaboration",
    "Public Speaking",
    "Project Planning",
    "Time Management",
    "Professional Feedback",
    "Documentation",
    "Leadership",
    "Critical Thinking",
    "Presentation Skills",
  ];

  const innovationSkills = [
    "Portfolio websites",
    "Live web applications",
    "GitHub repositories",
    "Team projects",
    "Presentations",
    "Demonstrations",
  ];

  const competencyBlocks = [
    {
      title: "Technology Skills",
      icon: Code2,
      items: techSkills,
    },
    {
      title: "Professional Development",
      icon: Users,
      items: professionalSkills,
    },
    {
      title: "Innovation & Portfolio Building",
      icon: FolderKanban,
      items: innovationSkills,
    },
  ];

  const mentorshipItems = [
    "Weekly reviews",
    "Code reviews",
    "Design critiques",
    "Reflection journals",
    "Career discussions",
    "Guest speakers",
    "Team collaboration",
    "Professional expectations",
  ];

  const communityImpactItems = [
    "Community platforms",
    "Educational resources",
    "Local problem solving",
    "Digital inclusion",
    "Ethical AI",
    "Neighborhood innovation",
  ];

  const surveyHighlights = [
    {
      stat: "92%",
      caption: "Interns reported improved confidence and leadership skills",
    },
    {
      stat: "9/10",
      caption: "Participants said the program changed how they think about their future in tech",
    },
    {
      stat: "100%",
      caption: "Interns improved teamwork and problem-solving abilities",
    },
    {
      stat: "80%+",
      caption: "Built portfolios or pursued college tech courses after the program",
    },
  ];

  const seniorFocusAreas = [
    "Digital confidence",
    "Connection and belonging",
    "Lifelong learning",
    "Practical technology support",
    "Community-centered workshops",
  ];

  const seniorOutcomeStats = [
    { value: "2 Years", label: "Montopolis partnership" },
    { value: "50+", label: "Workshops delivered" },
    { value: "40+", label: "Technology discussions" },
    { value: "100+", label: "One-on-one tech support sessions" },
    { value: "100+", label: "Seniors served" },
  ];

  const ambassadorInitiatives = [
    "Neighborhood leadership",
    "Peer mentorship",
    "Community events",
    "Civic participation",
    "Local problem solving",
  ];

  const ambassadorOutcomeStats = [
    { value: "25", label: "Community Ambassadors trained" },
  ];

  return (
    <Container fluid className="about-section impact-page impact-stats-page impact-v2-page">
      <Container className="impact-container impact-v2-container">
        <header className="impact-v2-hero">
          <p className="impact-v2-eyebrow">Community Impact</p>
          <h1 className="impact-v2-title">Designing technology pathways that create real opportunity</h1>
          <p className="impact-v2-lead">
            Choicest Fruit delivers workforce development, digital inclusion, and community innovation programs that
            turn learning into measurable social and economic impact.
          </p>

          <div className="impact-v2-chip-row" aria-label="Impact quick facts">
            <span className="impact-v2-chip">Workforce Development & Youth Innovation</span>
            <span className="impact-v2-chip">Digital Inclusion for Seniors</span>
            <span className="impact-v2-chip">Community Ambassadors</span>
          </div>
        </header>

        <section className="impact-v2-section" aria-label="Core impact focus areas">
          <Row className="g-3">
            {focusAreas.map((item) => {
              const Icon = item.icon;
              return (
                <Col md={4} key={item.title}>
                  <div className="policy-card impact-v2-focus-card h-100">
                    <div className="impact-v2-icon-wrap" aria-hidden="true">
                      <Icon size={19} strokeWidth={2.3} />
                    </div>
                    <h2 className="impact-v2-focus-title">{item.title}</h2>
                    <p className="about-text impact-v2-focus-body">{item.body}</p>
                  </div>
                </Col>
              );
            })}
          </Row>
        </section>

        <section className="impact-v2-section" aria-label="Travis County SYEP internship partnership">
          <Row className="g-3 align-items-stretch">
            <Col lg={7}>
              <div className="policy-card impact-v2-main-card h-100">
                <p className="impact-v2-kicker">Travis County Summer Youth Employment Program (2024–2026)</p>
                <h2 className="impact-v2-section-title">Travis County SYEP Internship Partnership</h2>
                <p className="about-text impact-v2-copy">
                  For three consecutive years, Choicest Fruit has partnered with Travis County&apos;s Summer Youth
                  Employment Program (SYEP) to deliver technology internships. Interns gain hands-on experience
                  while working alongside mentors on meaningful projects that serve real community needs.
                </p>

                <div className="impact-v2-subpanel impact-v2-subpanel--feature">
                  <div className="impact-v2-subpanel-head">
                    <h3 className="impact-v2-subtitle mb-0">Learning Through Real Projects</h3>
                    <span className="impact-v2-inline-badge">Industry-aligned project delivery</span>
                  </div>

                  <div className="impact-v2-pill-grid" role="list" aria-label="Real-world project examples">
                    {projectExamples.map((item) => (
                      <span key={item} className="impact-v2-pill" role="listitem">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="impact-v2-stat-grid" role="list" aria-label="SYEP impact metrics">
                  {syepOutcomeStats.map((metric) => (
                    <div key={`${metric.value}-${metric.label}`} className="impact-v2-stat-card" role="listitem">
                      <div className="impact-v2-stat-value">{metric.value}</div>
                      <div className="impact-v2-stat-label">{metric.label}</div>
                    </div>
                  ))}
                </div>

                <div className="impact-v2-proof-row" role="list" aria-label="Program quality signals">
                  <span className="impact-v2-proof" role="listitem">Guest industry speakers</span>
                  <span className="impact-v2-proof" role="listitem">Career presentations</span>
                  <span className="impact-v2-proof" role="listitem">Mentor-led project reviews</span>
                </div>
              </div>
            </Col>

            <Col lg={5}>
              <div className="policy-card impact-v2-skill-card h-100">
                <p className="impact-v2-kicker">Capability Framework</p>
                <h3 className="impact-v2-section-title">Competency pathways built for real-world outcomes</h3>
                <p className="about-text impact-v2-copy impact-v2-skill-lead">
                  Interns develop technical depth, professional fluency, and portfolio-ready proof of work.
                </p>

                <div className="impact-v2-skill-grid" role="list" aria-label="Intern competency categories">
                  {competencyBlocks.map((block) => {
                    const Icon = block.icon;
                    return (
                      <section className="impact-v2-skill-block" key={block.title} role="listitem">
                        <div className="impact-v2-skill-head">
                          <span className="impact-v2-skill-icon" aria-hidden="true">
                            <Icon size={16} strokeWidth={2.4} />
                          </span>
                          <h4 className="impact-v2-skill-title">{block.title}</h4>
                        </div>

                        <div className="impact-v2-skill-tags">
                          {block.items.map((item) => (
                            <span key={`${block.title}-${item}`} className="impact-v2-skill-tag">
                              {item}
                            </span>
                          ))}
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="impact-v2-skill-footer">
                  <Sparkles size={16} strokeWidth={2.3} />
                  <span>Outcome: internship-to-career readiness with demonstrable projects.</span>
                </div>
              </div>
            </Col>
          </Row>
        </section>

        <section className="impact-v2-section" aria-label="Community impact and mentorship">
          <Row className="g-3">
            <Col md={6}>
              <div className="policy-card impact-v2-panel impact-v2-panel--impact h-100">
                <div className="impact-v2-panel-head">
                  <div className="impact-v2-icon-title mb-0">
                    <ShieldCheck size={18} strokeWidth={2.3} />
                    <h3 className="impact-v2-subtitle mb-0">Community Impact</h3>
                  </div>
                  <span className="impact-v2-inline-badge">Local Value Creation</span>
                </div>
                <p className="about-text impact-v2-copy">Building Technology That Serves Communities</p>

                <div className="impact-v2-feature-grid" role="list" aria-label="Community impact priorities">
                  {communityImpactItems.map((item) => (
                    <span className="impact-v2-feature-tag" key={item} role="listitem">
                      {item}
                    </span>
                  ))}
                </div>

                <div className="impact-v2-panel-foot">
                  <ShieldCheck size={15} strokeWidth={2.4} />
                  <span>Outcome: practical technology solutions that strengthen neighborhood resilience.</span>
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="policy-card impact-v2-panel impact-v2-panel--mentorship h-100">
                <div className="impact-v2-panel-head">
                  <div className="impact-v2-icon-title mb-0">
                    <MessageSquareText size={18} strokeWidth={2.3} />
                    <h3 className="impact-v2-subtitle mb-0">Industry-Style Mentorship</h3>
                  </div>
                  <span className="impact-v2-inline-badge">Career Readiness</span>
                </div>

                <div className="impact-v2-feature-grid" role="list" aria-label="Mentorship components">
                  {mentorshipItems.map((item) => (
                    <span className="impact-v2-feature-tag" key={item} role="listitem">
                      {item}
                    </span>
                  ))}
                </div>

                <div className="impact-v2-panel-foot">
                  <MessageSquareText size={15} strokeWidth={2.4} />
                  <span>Outcome: stronger collaboration, communication, and professional confidence.</span>
                </div>
              </div>
            </Col>
          </Row>
        </section>

        <section className="impact-v2-section" aria-label="Internship survey highlights">
          <div className="impact-v2-heading-wrap">
            <h2 className="impact-v2-section-title">Internship Survey Highlights</h2>
            <p className="about-text impact-v2-copy impact-v2-copy--center">
              Participant surveys showing measurable growth.
            </p>
          </div>
          <Row className="g-3" role="list" aria-label="Survey highlights">
            {surveyHighlights.map((item) => (
              <Col sm={6} lg={3} key={item.stat} role="listitem">
                <div className="policy-card impact-v2-survey-card h-100">
                  <div className="impact-v2-survey-stat">{item.stat}</div>
                  <p className="impact-v2-survey-caption">{item.caption}</p>
                </div>
              </Col>
            ))}
          </Row>
        </section>

        <section className="impact-v2-section" aria-label="Senior and ambassador metrics">
          <Row className="g-3">
            <Col lg={8}>
              <div className="policy-card impact-v2-main-card impact-v2-main-card--senior h-100">
                <div className="impact-v2-panel-head">
                  <div>
                    <p className="impact-v2-kicker">Senior Digital Inclusion & Technology</p>
                    <h2 className="impact-v2-section-title mb-0">Building digital confidence, connection, and lifelong learning</h2>
                  </div>
                  <span className="impact-v2-inline-badge">Community Lifelong Learning</span>
                </div>

                <p className="about-text impact-v2-copy impact-v2-senior-copy">
                  Workshops, one-on-one support, and community conversations help seniors use technology with more
                  independence, comfort, and confidence.
                </p>

                <div className="impact-v2-feature-grid" role="list" aria-label="Senior digital inclusion priorities">
                  {seniorFocusAreas.map((item) => (
                    <span className="impact-v2-feature-tag" key={item} role="listitem">
                      {item}
                    </span>
                  ))}
                </div>

                <div className="impact-v2-stat-grid impact-v2-stat-grid--senior" role="list" aria-label="Senior inclusion metrics">
                  {seniorOutcomeStats.map((metric) => (
                    <div key={`${metric.value}-${metric.label}`} className="impact-v2-stat-card" role="listitem">
                      <div className="impact-v2-stat-value">{metric.value}</div>
                      <div className="impact-v2-stat-label">{metric.label}</div>
                    </div>
                  ))}
                </div>

                <div className="impact-v2-panel-foot">
                  <HeartHandshake size={15} strokeWidth={2.4} />
                  <span>Outcome: stronger digital confidence and more connected community participation.</span>
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <div className="policy-card impact-v2-panel impact-v2-panel--ambassador h-100">
                <div className="impact-v2-panel-head">
                  <div>
                    <p className="impact-v2-kicker">Ambassador Community Initiatives</p>
                    <h3 className="impact-v2-subtitle mb-0">Ambassador Growth</h3>
                  </div>
                  <span className="impact-v2-inline-badge">Leadership Pipeline</span>
                </div>

                <p className="about-text impact-v2-copy impact-v2-ambassador-copy">
                  Ambassadors develop the skills to lead, represent, and expand community-driven innovation.
                </p>

                <div className="impact-v2-stat-grid impact-v2-stat-grid--single" role="list" aria-label="Ambassador growth metrics">
                  {ambassadorOutcomeStats.map((metric) => (
                    <div key={`${metric.value}-${metric.label}`} className="impact-v2-stat-card" role="listitem">
                      <div className="impact-v2-stat-value">{metric.value}</div>
                      <div className="impact-v2-stat-label">{metric.label}</div>
                    </div>
                  ))}
                </div>

                <div className="impact-v2-feature-grid impact-v2-feature-grid--compact" role="list" aria-label="Ambassador initiatives">
                  {ambassadorInitiatives.map((item) => (
                    <span className="impact-v2-feature-tag" key={item} role="listitem">
                      {item}
                    </span>
                  ))}
                </div>

                <div className="impact-v2-panel-foot">
                  <Users size={15} strokeWidth={2.4} />
                  <span>Outcome: ambassadors who can mentor peers and champion local impact.</span>
                </div>
              </div>
            </Col>
          </Row>
        </section>

        <section className="impact-v2-section impact-testimonials-section" aria-label="Intern testimonials">
          <p className="impact-v2-kicker text-center">Intern Testimonials</p>
          <h2 className="impact-v2-section-title text-center">Intern Voices and Program Experience</h2>
          <div className="impact-testimonials">
            <Testimonials reviews={reviews} />
          </div>
        </section>

        <section className="impact-v2-section" aria-label="Call to action">
          <div className="policy-card impact-v2-cta-band">
            <div>
              <p className="impact-v2-kicker">Partner with our impact model</p>
              <h3 className="impact-v2-cta-title mb-0">Bring workforce development and digital inclusion to your community</h3>
            </div>
            <div className="impact-v2-cta-actions">
              <Button onClick={() => navigate("/programs/syep-internship")} className="custom-yellow-btn impact-v2-btn">
                Explore Internship Program <ArrowRight size={16} />
              </Button>
              <Button onClick={() => navigate("/contact")} className="btn impact-v2-btn-ghost">
                Start a Partnership
              </Button>
            </div>
          </div>
        </section>

        <footer className="impact-v2-footer">Community-centered technology education with measurable outcomes.</footer>
      </Container>
    </Container>
  );
}
